import { executeJSmodules } from 'util.misc'

const GRID_SELECTOR = '.new-grid.product-grid.collection-grid'
const GRID_ITEM_SELECTOR = '.grid-item.grid-product'
const PAGINATION_SELECTOR = '.element-pagination'
const NEXT_LINK_SELECTOR = 'a[rel="next"], a[data-collection-next-link]'
const SENTINEL_SELECTOR = '[data-collection-infinite-sentinel]'
const LOADER_SELECTOR = '[data-collection-infinite-loader]'
const SCROLL_TOP_BUTTON_SELECTOR = '[data-scroll-top-button]'
const PAGINATION_WRAPPER_SELECTOR = '[data-collection-pagination]'
const RELOAD_EVENT = 'collection:reloaded'
const SKU_SORT_VALUES = new Set(['sku-ascending', 'sku-descending'])
const RESULTS_EVENT = 'collection:computed-filters:results'
const PAGINATION_CLEANUP_SELECTOR = '.element-pagination, [data-collection-pagination], .pagination, .paginate'

class KCCollectionInfiniteScroll {
  constructor() {
    this.debugEnabled = new URLSearchParams(window.location.search).get('kc_debug') === '1'
    this.loading = false
    this.nextUrl = null
    this.loadedUrls = new Set()
    this.observer = null
    this.grid = null
    this.sentinel = null
    this.loader = null
    this.emptyState = null
    this.loaderVisible = false
    this.loaderTimer = null
    this.loaderLoadingText = 'Loading more products...'
    this.loaderEndedText = 'All products loaded.'
    this.loaderEmptyText = 'No products available at the moment.'
    this.ended = false
    this.endMode = null
    this.scrollTopButton = null
    this.scrollTopVisible = false
    this.paginationHiddenLogged = false
    this.pageSize = 12
    this.preloadingSkuSort = false
    this.loadFailed = false

    this.handleCollectionReload = this.handleCollectionReload.bind(this)
    this.handleComputedResults = this.handleComputedResults.bind(this)
    this.handleSkuSortSelected = this.handleSkuSortSelected.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.handleScrollTopClick = this.handleScrollTopClick.bind(this)
  }

  init() {
    this.bindScrollTopButton()
    if (!('IntersectionObserver' in window)) return

    document.addEventListener(RELOAD_EVENT, this.handleCollectionReload)
    document.addEventListener(RESULTS_EVENT, this.handleComputedResults)
    document.addEventListener('collection:sku-sort-selected', this.handleSkuSortSelected)
    this.mount()
  }

  debug(message, payload = {}) {
    if (!this.debugEnabled) return
    console.log('[KC InfiniteScroll]', message, payload)
  }

  mount() {
    this.grid = document.querySelector(GRID_SELECTOR)
    this.sentinel = document.querySelector(SENTINEL_SELECTOR)
    this.loader = document.querySelector(LOADER_SELECTOR)
    this.emptyState = document.querySelector('[data-kc-empty-state]')
    const collectionSurface = document.querySelector('[data-kc-collection-surface][data-infinite-scroll="true"]')
    const pageSizeFromDom = Number.parseInt(collectionSurface?.dataset?.infinitePageSize || '12', 10)
    this.pageSize = Number.isFinite(pageSizeFromDom) && pageSizeFromDom > 0 ? pageSizeFromDom : 12
    this.loaderLoadingText = this.loader?.dataset?.loadingText || this.loaderLoadingText
    this.loaderEndedText = this.loader?.dataset?.endedText || this.loaderEndedText
    this.loaderEmptyText = this.loader?.dataset?.emptyText || this.loaderEmptyText
    this.nextUrl = this.findNextUrl(document)
    this.loading = false
    this.ended = false
    this.endMode = null
    this.preloadingSkuSort = false
    this.loadFailed = false
    this.toggleEmptyState(false)
    this.setLoaderMessage(this.loaderLoadingText)

    const initialItemCount = this.grid ? this.grid.querySelectorAll(GRID_ITEM_SELECTOR).length : 0
    this.debug('init', {
      gridFound: Boolean(this.grid),
      sentinelFound: Boolean(this.sentinel),
      initialItemCount
    })
    this.debug(`pageSize=${this.pageSize}`)
    this.debug('nextUrl detected', { nextUrl: this.nextUrl })
    this.logPaginationHiddenOnce()

    if (!this.grid || !this.sentinel || !this.nextUrl) {
      const hasVisibleItems = this.getVisibleItemCount() > 0
      this.stopAtEnd('no-next-url', { mode: hasVisibleItems ? 'complete' : 'silent' })
      return
    }

    if (this.isCustomSkuSort()) {
      this.cleanupPaginationNodes()
      this.preloadSkuSortedCollection()
      return
    }

    this.disconnectObserver()
    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      root: null,
      rootMargin: '0px 0px 960px 0px',
      threshold: 0
    })
    this.observer.observe(this.sentinel)
    this.cleanupPaginationNodes()
  }

  bindScrollTopButton() {
    if (this.scrollTopButton) {
      this.scrollTopButton.removeEventListener('click', this.handleScrollTopClick)
      window.removeEventListener('scroll', this.handleScroll, { passive: true })
    }

    this.scrollTopButton = document.querySelector(SCROLL_TOP_BUTTON_SELECTOR)
    this.scrollTopVisible = false

    if (!this.scrollTopButton) return

    this.scrollTopButton.hidden = true
    this.scrollTopButton.classList.remove('is-visible')
    this.scrollTopButton.addEventListener('click', this.handleScrollTopClick)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    this.handleScroll()
  }

  handleScrollTopClick() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  handleScroll() {
    if (!this.scrollTopButton) return

    const shouldShow = window.scrollY >= window.innerHeight * 3
    if (shouldShow === this.scrollTopVisible) return

    this.scrollTopVisible = shouldShow
    this.scrollTopButton.hidden = !shouldShow
    this.scrollTopButton.classList.toggle('is-visible', shouldShow)
    this.debug('back-to-top visibility', { visible: shouldShow })
  }

  handleCollectionReload() {
    this.debug('reset on collection:reloaded')
    this.loadedUrls = new Set()
    this.nextUrl = null
    this.loading = false
    this.loaderVisible = false
    this.ended = false
    this.endMode = null
    this.preloadingSkuSort = false
    this.loadFailed = false
    this.toggleEmptyState(false)
    this.setLoadingState(false)
    this.mount()
  }

  handleComputedResults(event) {
    const visibleCount = Number(event?.detail?.visibleCount ?? this.getVisibleItemCount())
    const activeComputedFilters = Boolean(event?.detail?.activeComputedFilters)

    if (visibleCount > 0) {
      this.toggleEmptyState(false)
      if (!this.nextUrl && !this.loading) {
        this.stopAtEnd('results-visible-end-of-list', { mode: 'complete' })
      } else if (this.endMode === 'empty') {
        this.endMode = null
        this.ended = false
        this.setLoadingState(false)
      }
      return
    }

    if (!activeComputedFilters) {
      this.toggleEmptyState(false)
      if (!this.nextUrl && !this.loading) {
        this.stopAtEnd('results-no-visible-items', { mode: 'silent' })
      }
      return
    }

    if (this.nextUrl) {
      this.toggleEmptyState(false)
      if (!this.loading) {
        this.loadNextPage()
      }
      return
    }

    this.stopAtEnd('results-empty-after-filters', { mode: 'empty' })
  }

  logPaginationHiddenOnce() {
    if (this.paginationHiddenLogged) return
    const pagination = document.querySelector(PAGINATION_WRAPPER_SELECTOR)
    if (!pagination) return
    this.paginationHiddenLogged = true
    this.debug('pagination hidden: true')
  }

  hasActiveComputedFilters() {
    return Boolean(window.__kcCollectionComputedFilters?.hasActiveComputedFilters?.())
  }

  getVisibleItemCount() {
    if (!this.grid) return 0
    return [...this.grid.querySelectorAll(GRID_ITEM_SELECTOR)].filter(
      (item) => !item.hidden && item.getAttribute('aria-hidden') !== 'true'
    ).length
  }

  toggleEmptyState(shouldShow) {
    if (!this.emptyState) return
    this.emptyState.hidden = !shouldShow
  }

  stopAtEnd(reason = 'end-of-list', { mode = 'complete' } = {}) {
    this.nextUrl = null
    this.loading = false
    this.ended = true
    this.endMode = mode
    this.disconnectObserver()

    if (mode === 'empty') {
      this.toggleEmptyState(true)
      this.setLoaderMessage(this.loaderEmptyText)
      if (this.loader) {
        this.loader.hidden = true
        this.loader.setAttribute('aria-busy', 'false')
      }
      this.loaderVisible = false
      this.debug('end reached; showing empty state', { reason })
      return
    }

    this.toggleEmptyState(false)
    if (mode === 'silent') {
      if (this.loader) {
        this.loader.hidden = true
        this.loader.setAttribute('aria-busy', 'false')
      }
      this.loaderVisible = false
      this.debug('end reached silently', { reason })
      return
    }

    this.setLoaderMessage(this.loaderEndedText)
    if (this.loader) {
      this.loader.hidden = false
      this.loader.setAttribute('aria-busy', 'false')
    }
    this.loaderVisible = true
    this.debug('end reached, stop + show loader copy', { reason })
    this.debug("end reached; showing 'All products loaded.'", { reason })
  }

  disconnectObserver() {
    if (!this.observer) return
    this.observer.disconnect()
    this.observer = null
  }

  onIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return
    this.loadNextPage()
  }

  isCustomSkuSort() {
    return SKU_SORT_VALUES.has(new URLSearchParams(window.location.search).get('sort_by') || '')
  }

  handleSkuSortSelected(event) {
    if (!SKU_SORT_VALUES.has(event?.detail?.sortValue || '')) return
    this.preloadSkuSortedCollection()
  }

  async preloadSkuSortedCollection() {
    if (this.preloadingSkuSort || this.loadFailed || !this.nextUrl) return

    this.preloadingSkuSort = true
    this.disconnectObserver()
    if (this.grid) {
      this.grid.hidden = true
      this.grid.setAttribute('aria-busy', 'true')
    }
    this.setLoadingState(true)

    try {
      while (this.nextUrl && !this.loadFailed) {
        await this.loadNextPage({ showLoader: false })
      }
    } finally {
      this.preloadingSkuSort = false
      if (this.grid) {
        this.grid.hidden = false
        this.grid.removeAttribute('aria-busy')
      }
      this.setLoadingState(false)
    }
  }

  normalizeUrl(url) {
    if (!url) return null
    try {
      const normalized = new URL(url, window.location.href)
      normalized.hash = ''
      return normalized.toString()
    } catch (_error) {
      return null
    }
  }

  findNextUrl(scope) {
    const link = scope.querySelector(`${PAGINATION_SELECTOR} ${NEXT_LINK_SELECTOR}`) || scope.querySelector(NEXT_LINK_SELECTOR)
    if (!link?.href) return null
    return this.normalizeUrl(link.href)
  }

  removeCurrentPagination() {
    const pagination = this.grid?.querySelector(PAGINATION_SELECTOR)
    if (pagination) pagination.remove()
  }

  setLoaderMessage(message) {
    if (!this.loader || !message) return
    this.loader.textContent = message
  }

  setLoadingState(isLoading) {
    if (!this.loader) return

    if (this.loadFailed) {
      this.setLoaderMessage('Unable to load the complete SKU order. Please refresh and try again.')
      this.loader.hidden = false
      this.loader.setAttribute('aria-busy', 'false')
      this.loaderVisible = true
      return
    }

    if (this.preloadingSkuSort) {
      this.setLoaderMessage('Preparing complete SKU order...')
      this.loader.hidden = false
      this.loader.setAttribute('aria-busy', 'true')
      this.loaderVisible = true
      return
    }

    if (this.loaderTimer) {
      window.clearTimeout(this.loaderTimer)
      this.loaderTimer = null
    }
    if (this.ended) {
      if (this.endMode === 'complete') {
        this.setLoaderMessage(this.loaderEndedText)
        this.loader.hidden = false
        this.loader.setAttribute('aria-busy', 'false')
        this.loaderVisible = true
      } else {
        this.loader.hidden = true
        this.loader.setAttribute('aria-busy', 'false')
        this.loaderVisible = false
      }
      return
    }

    const shouldShow = Boolean(isLoading && this.nextUrl)
    this.toggleEmptyState(false)
    this.setLoaderMessage(this.loaderLoadingText)
    this.loader.setAttribute('aria-busy', shouldShow ? 'true' : 'false')

    if (shouldShow) {
      this.loader.hidden = true
      this.loaderTimer = window.setTimeout(() => {
        this.loaderTimer = null
        if (!this.loading || this.ended || !this.nextUrl || !this.loader) return
        this.loader.hidden = false
      }, 220)
    } else {
      this.loader.hidden = true
    }

    if (this.loaderVisible !== shouldShow) {
      this.loaderVisible = shouldShow
      this.debug('loader visibility', { visible: shouldShow, hasNext: Boolean(this.nextUrl) })
    }
  }

  cleanupPaginationNodes() {
    if (!this.grid) return 0
    const nodes = [...this.grid.querySelectorAll(PAGINATION_CLEANUP_SELECTOR)]
    let removed = 0
    nodes.forEach((node) => {
      if (!node.parentNode) return
      node.parentNode.removeChild(node)
      removed += 1
    })
    this.debug(`pagination cleanup removed ${removed} nodes`)
    return removed
  }

  async loadNextPage({ showLoader = true } = {}) {
    if (this.loading || !this.nextUrl || this.loadFailed) return

    const requestUrl = this.nextUrl
    if (this.loadedUrls.has(requestUrl)) {
      this.stopAtEnd('duplicate-next-url')
      return
    }

    this.loading = true
    this.setLoadingState(showLoader)
    this.debug('load start', { requestUrl })

    try {
      const response = await fetch(requestUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const html = await response.text()
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html')
      const nextGrid = parsedDocument.querySelector(GRID_SELECTOR)

      if (!nextGrid) {
        this.stopAtEnd('missing-grid')
        return
      }

      const incomingItems = [...nextGrid.querySelectorAll(GRID_ITEM_SELECTOR)].map((item) => item.cloneNode(true))
      const upcomingUrl = this.findNextUrl(parsedDocument)

      if (!incomingItems.length) {
        this.loadedUrls.add(requestUrl)
        this.nextUrl = upcomingUrl
        this.debug('skip empty page', {
          requestUrl,
          nextUrl: this.nextUrl
        })

        if (!this.nextUrl) {
          const mode = this.getVisibleItemCount() === 0 && this.hasActiveComputedFilters() ? 'empty' : 'complete'
          this.stopAtEnd('no-new-grid-items', { mode })
          return
        }

        this.loading = false
        this.setLoadingState(false)
        return this.loadNextPage({ showLoader: false })
      }

      const fragment = document.createDocumentFragment()
      incomingItems.forEach((item) => fragment.appendChild(item))

      const moduleScripts = fragment.querySelectorAll('script[type="module"]')
      this.removeCurrentPagination()
      this.grid.appendChild(fragment)

      executeJSmodules(moduleScripts)
      this.cleanupPaginationNodes()
      document.dispatchEvent(
        new CustomEvent('collection:appended', {
          detail: {
            items: incomingItems
          }
        })
      )

      this.loadedUrls.add(requestUrl)
      this.nextUrl = upcomingUrl
      this.debug('load finish', {
        requestUrl,
        appendedCount: incomingItems.length,
        nextUrl: this.nextUrl
      })
      this.debug(`appended ${incomingItems.length} cards; pagination hidden/removed`)

      if (!this.nextUrl) {
        const mode = this.getVisibleItemCount() === 0 && this.hasActiveComputedFilters() ? 'empty' : 'complete'
        this.stopAtEnd('no-next-url-after-append', { mode })
        return
      }
    } catch (error) {
      this.debug('load failure', { requestUrl, error: String(error) })
      this.loadFailed = true
      this.disconnectObserver()
    } finally {
      this.loading = false
      this.setLoadingState(false)

      if (!this.ended && !this.loadFailed && this.nextUrl && this.hasActiveComputedFilters() && this.getVisibleItemCount() === 0) {
        this.loadNextPage()
      }
    }
  }
}

if (!window.__kcCollectionInfiniteScroll) {
  window.__kcCollectionInfiniteScroll = new KCCollectionInfiniteScroll()
  window.__kcCollectionInfiniteScroll.init()
}
